import {
  listarEmpresasPaginadas,
  listarEmpresasSimples,
  buscarEmpresaPorNomeCnpj,
  buscarEmpresaPorId,
} from "./empresaService.js";
import { listarAcompanhamentosMe } from "./acompanhamentoService.js";
import { normalizarListaEmpresas, normalizarEmpresaResposta } from "../utils/empresa.js";
import {
  obterIdentidadeSessao,
  obterNivelAcessoPagina,
  obterDocumentoUsuarioLogadoPagina,
} from "../utils/session.js";
import {
  NIVEL_EMPRESA,
  NIVEL_CONSULTOR,
  NIVEL_SUPEX,
  empresaFoiCadastradaPeloConsultor,
} from "../utils/permissoes.js";
import {
  normalizarEmpresaAcompanhamentoItem,
  deduplicarAcompanhamentosPorEmpresa,
} from "../mappers/empresaMapper.js";
import {
  cadastroRequerSupex,
  normalizarListaSolicitacoesExcecao,
} from "../mappers/supexMapper.js";
import { montarDescricaoEmpresa } from "../utils/empresa.js";

/**
 * Resolve a empresa do contexto atual para o nível empresa.
 * Empresa sempre fica presa ao próprio documento, ignorando
 * query/storage de terceiros (espelha o CadastroEmpresa).
 */
async function resolverEmpresaDoNivelEmpresa(token) {
  const documentoUsuario = obterDocumentoUsuarioLogadoPagina();
  if (!documentoUsuario) return null;

  const empresaDoUsuario = await buscarEmpresaPorNomeCnpj(documentoUsuario, token);
  if (!empresaDoUsuario?.id) return null;

  if (typeof window !== "undefined") {
    window.localStorage.setItem(
      "empresa_id_acompanhamento",
      String(empresaDoUsuario.id),
    );
    window.localStorage.setItem(
      "empresa_busca_acompanhamento",
      montarDescricaoEmpresa(empresaDoUsuario),
    );
  }

  return empresaDoUsuario;
}

async function listarEmpresasDoConsultor({
  token,
  page,
  limit,
  usuarioIdLogado,
  documentoLogado,
}) {
  const filtrar = (lista) =>
    lista
      .map(normalizarEmpresaAcompanhamentoItem)
      .filter(Boolean)
      .filter((item) =>
        empresaFoiCadastradaPeloConsultor(item, usuarioIdLogado, documentoLogado),
      );

  try {
    const listaPaginada = await listarEmpresasPaginadas({ token, page, limit });
    return deduplicarAcompanhamentosPorEmpresa(
      filtrar(normalizarListaEmpresas(listaPaginada)),
    );
  } catch (error) {
    console.warn(
      "Falha na listagem paginada do consultor, tentando fallback simples:",
      error,
    );
  }

  try {
    const listaSimples = await listarEmpresasSimples(token);
    return deduplicarAcompanhamentosPorEmpresa(
      filtrar(normalizarListaEmpresas(listaSimples)),
    );
  } catch (error) {
    console.warn("Fallback simples do consultor também falhou:", error);
  }

  return [];
}

function aplicarFiltroPorNivel(lista, nivel, identidade) {
  if (nivel === NIVEL_CONSULTOR) {
    return lista.filter((item) =>
      empresaFoiCadastradaPeloConsultor(
        item,
        identidade.usuarioId,
        identidade.documento,
      ),
    );
  }
  if (nivel === NIVEL_SUPEX) {
    return lista.filter((item) => {
      const raw = item?.raw || item || {};
      return cadastroRequerSupex(raw, raw, [], item);
    });
  }
  return lista;
}

/**
 * Carrega acompanhamentos a partir da rota dedicada /acompanhamentos/me
 * com fallback para listagem geral de empresas.
 */
async function listarAcompanhamentosGenericos({
  token,
  page,
  limit,
  nivel,
  identidade,
  busca,
  empresaId,
}) {
  try {
    const payload = await listarAcompanhamentosMe({
      token,
      page,
      limit,
      empresaId,
      busca,
    });
    const lista = normalizarListaEmpresas(payload)
      .map(normalizarEmpresaAcompanhamentoItem)
      .filter(Boolean);
    return deduplicarAcompanhamentosPorEmpresa(
      aplicarFiltroPorNivel(lista, nivel, identidade),
    );
  } catch {
    /* tenta fallback abaixo */
  }

  try {
    const lista = await listarEmpresasPaginadas({
      token,
      page,
      limit,
      busca,
      empresaId,
    });
    const itens = normalizarListaEmpresas(lista)
      .map(normalizarEmpresaAcompanhamentoItem)
      .filter(Boolean);
    return deduplicarAcompanhamentosPorEmpresa(
      aplicarFiltroPorNivel(itens, nivel, identidade),
    );
  } catch {
    /* tenta fallback secundário */
  }

  try {
    const lista = await listarEmpresasSimples(token);
    const itens = normalizarListaEmpresas(lista)
      .map(normalizarEmpresaAcompanhamentoItem)
      .filter(Boolean);
    return deduplicarAcompanhamentosPorEmpresa(
      aplicarFiltroPorNivel(itens, nivel, identidade),
    );
  } catch (error) {
    console.warn("Fallback simples de empresas também falhou:", error);
  }

  return [];
}

/**
 * Carrega acompanhamentos do usuário conforme o nível de acesso.
 */
export async function carregarAcompanhamentosDoUsuario(
  token,
  page = 1,
  limit = 50,
  contexto = {},
) {
  const nivel = obterNivelAcessoPagina();
  const identidade = obterIdentidadeSessao();

  if (nivel === NIVEL_EMPRESA) {
    const unica = await resolverEmpresaDoNivelEmpresa(token);
    return unica?.id ? [{ ...unica, raw: unica }] : [];
  }

  if (nivel === NIVEL_CONSULTOR) {
    return await listarEmpresasDoConsultor({
      token,
      page,
      limit,
      usuarioIdLogado: identidade.usuarioId,
      documentoLogado: identidade.documento,
    });
  }

  return await listarAcompanhamentosGenericos({
    token,
    page,
    limit,
    nivel,
    identidade,
    busca: contexto.busca,
    empresaId: contexto.empresaId,
  });
}

/**
 * Atualiza o status de uma empresa específica e devolve um modelo
 * pronto para a UI (passando pelos mappers de SUPEX, rejeição e status).
 */
export async function buscarStatusCompletoDaEmpresa(empresaRef, token) {
  if (!empresaRef?.id) throw new Error("Empresa inválida");

  try {
    const payload = await buscarEmpresaPorId(empresaRef.id, token);
    const empresa = normalizarEmpresaResposta(payload) || payload;

    const listaExcecoes = normalizarListaSolicitacoesExcecao(payload, empresa);

    return {
      payload,
      empresa,
      listaExcecoes,
      fonte: "api",
    };
  } catch (error) {
    // Endpoint /tca-empregador/:id não está exposto no backend (404).
    // Não falhamos a UI por isso — caímos no `raw` que já veio da listagem.
    const eh404 =
      error?.response?.status === 404 ||
      /status code 404/i.test(error?.message || "");
    if (!eh404) throw error;

    const raw = empresaRef.raw || empresaRef || {};
    const empresa = normalizarEmpresaResposta(raw) || raw;
    const listaExcecoes = normalizarListaSolicitacoesExcecao(raw, empresa);

    return {
      payload: raw,
      empresa,
      listaExcecoes,
      fonte: "cache",
    };
  }
}

/**
 * Lê a lista de exceções de uma empresa no contexto da listagem atual.
 * Útil quando a UI já tem o `raw` em memória.
 */
export function obterExcecoesDoItem(item) {
  const raw = item?.raw || {};
  return normalizarListaSolicitacoesExcecao(raw, raw);
}

export function obterRequerSupexDoItem(item) {
  const raw = item?.raw || item || {};
  const excecoes = obterExcecoesDoItem(item);
  return cadastroRequerSupex(raw, raw, excecoes, item);
}
